/**
 * Discover Layer Predicates — compile filter schemas into marker predicates.
 *
 * This module provides a single shared predicate pipeline used by both the
 * map view and table view to ensure consistent custom-layer filtering.
 * Client-side filtering is presentation-only and never an authorization boundary.
 *
 * @see docs/IMPLEMENT_MARKER_LAYERS_DISCOVER.md §4.4, §7
 */

import { MATCH_MODES } from './discoverLayerFilterSchema';

// ---------------------------------------------------------------------------
// Field value resolution
// ---------------------------------------------------------------------------

/**
 * Extract a field value from a marker object by field id.
 *
 * Field ids use the convention:
 * - Top-level: 'name', 'privacy', 'type', 'authorName', 'createdAt'
 * - Nested metadata: 'metadata.city', 'metadata.startDate', etc.
 *
 * @param {Object} marker
 * @param {string} fieldId
 * @returns {*} Resolved value or undefined
 */
export function resolveMarkerFieldValue(marker, fieldId) {
    if (!marker || !fieldId) return undefined;

    if (fieldId.startsWith('metadata.')) {
        const key = fieldId.slice('metadata.'.length);
        if (!marker.metadata || typeof marker.metadata !== 'object') return undefined;
        const val = marker.metadata[key];
        return val == null ? undefined : val;
    }

    const val = marker[fieldId];
    return val == null ? undefined : val;
}

// ---------------------------------------------------------------------------
// Single-rule predicate compilation
// ---------------------------------------------------------------------------

/**
 * Normalize a value to a trimmed lowercase string for comparison.
 */
function normStr(value) {
    if (value == null) return '';
    return String(value).trim().toLowerCase();
}

function normValues(value) {
    if (!Array.isArray(value)) {
        return [normStr(value)];
    }
    const normalized = value.map((item) => normStr(item)).filter(Boolean);
    return normalized.length > 0 ? normalized : [''];
}

function parseListValue(value) {
    return Array.isArray(value)
        ? value.map((v) => normStr(v)).filter(Boolean)
        : normStr(value).split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Parse a date/time value to a Unix timestamp (milliseconds).
 * Returns NaN for unparseable values.
 */
function toTimestamp(value) {
    if (value == null) return NaN;
    const ms = Date.parse(String(value));
    return ms;
}

/**
 * Compile a single filter rule into a predicate function.
 *
 * @param {Object} rule - Serialized rule: { fieldId, operator, value?, secondValue? }
 * @returns {function(Object): boolean} Predicate that tests a marker
 */
export function compileRulePredicate(rule) {
    if (!rule || !rule.fieldId || !rule.operator) {
        // Incomplete rules pass all markers (non-blocking)
        return () => true;
    }

    const { fieldId, operator } = rule;

    return (marker) => {
        const raw = resolveMarkerFieldValue(marker, fieldId);
        const rawValues = normValues(raw);

        switch (operator) {
            // --- String / generic equality ---
            case 'EQUALS':
                return rawValues.some((value) => value === normStr(rule.value));
            case 'NOT_EQUALS':
                return rawValues.every((value) => value !== normStr(rule.value));
            case 'CONTAINS':
                return rawValues.some((value) => value.includes(normStr(rule.value)));
            case 'STARTS_WITH':
                return rawValues.some((value) => value.startsWith(normStr(rule.value)));

            // --- List operators ---
            case 'IN': {
                const items = parseListValue(rule.value);
                return rawValues.some((value) => items.includes(value));
            }
            case 'NOT_IN': {
                const items = parseListValue(rule.value);
                return rawValues.every((value) => !items.includes(value));
            }

            // --- Numeric operators ---
            case 'GT': return Number(raw) > Number(rule.value);
            case 'GTE': return Number(raw) >= Number(rule.value);
            case 'LT': return Number(raw) < Number(rule.value);
            case 'LTE': return Number(raw) <= Number(rule.value);
            case 'BETWEEN': {
                const nRaw = Number(raw);
                const nLow = Number(rule.value);
                const nHigh = Number(rule.secondValue);
                // If any value is NaN (e.g. date strings), fall back to timestamp comparison
                if (Number.isNaN(nRaw) || Number.isNaN(nLow) || Number.isNaN(nHigh)) {
                    const tsRaw = toTimestamp(raw);
                    const tsLow = toTimestamp(rule.value);
                    const tsHigh = toTimestamp(rule.secondValue);
                    if (!Number.isNaN(tsRaw) && !Number.isNaN(tsLow) && !Number.isNaN(tsHigh)) {
                        return tsRaw >= tsLow && tsRaw <= tsHigh;
                    }
                    // Last resort: string comparison
                    const s = normStr(raw);
                    return s >= normStr(rule.value) && s <= normStr(rule.secondValue);
                }
                return nRaw >= nLow && nRaw <= nHigh;
            }

            // --- Date/time operators ---
            case 'BEFORE': {
                const tsRaw = toTimestamp(raw);
                const tsVal = toTimestamp(rule.value);
                if (!Number.isNaN(tsRaw) && !Number.isNaN(tsVal)) return tsRaw < tsVal;
                return normStr(raw) < normStr(rule.value);
            }
            case 'AFTER': {
                const tsRaw = toTimestamp(raw);
                const tsVal = toTimestamp(rule.value);
                if (!Number.isNaN(tsRaw) && !Number.isNaN(tsVal)) return tsRaw > tsVal;
                return normStr(raw) > normStr(rule.value);
            }

            // --- Boolean operators ---
            case 'IS_TRUE': {
                const s = normStr(raw);
                return s === 'true' || s === '1' || s === 'yes';
            }
            case 'IS_FALSE': {
                const s = normStr(raw);
                return s === '' || s === 'false' || s === '0' || s === 'no' || raw == null;
            }

            default:
                return true;
        }
    };
}

// ---------------------------------------------------------------------------
// Layer predicate compilation
// ---------------------------------------------------------------------------

/**
 * Compile a full filter schema into a single predicate function.
 *
 * @param {Object} filterSchema - { schemaVersion, match, rules }
 * @returns {function(Object): boolean} Predicate that tests a marker
 */
export function compileLayerPredicate(filterSchema) {
    if (!filterSchema || !Array.isArray(filterSchema.rules) || filterSchema.rules.length === 0) {
        // No rules → match all markers of the layer's type
        return () => true;
    }

    const predicates = filterSchema.rules.map(compileRulePredicate);
    const matchMode = filterSchema.match === MATCH_MODES.ANY ? 'ANY' : 'ALL';

    if (matchMode === 'ANY') {
        return (marker) => predicates.some((pred) => pred(marker));
    }
    return (marker) => predicates.every((pred) => pred(marker));
}

// ---------------------------------------------------------------------------
// Main filtering pipeline
// ---------------------------------------------------------------------------

/**
 * Normalize marker type for comparison.
 */
function normalizeType(type) {
    return typeof type === 'string' ? type.trim().toUpperCase() : '';
}

// ---------------------------------------------------------------------------
// Pre-compiled predicate cache (separated from the filtering pass so that
// viewport-driven marker changes do not trigger predicate recompilation)
// ---------------------------------------------------------------------------

/**
 * Pre-compile predicates for custom layers.
 *
 * Returns a stable structure that can be memoized independently of the
 * marker array.  When only the marker set changes (viewport pan/zoom)
 * the same compiled predicates are reused.
 *
 * @param {Array} customLayers - Full custom layer list (enabled + disabled)
 * @returns {{ enabledCompiled: Array, layerIds: string[] }}
 */
export function compileLayerPredicates(customLayers) {
    const safeLayers = Array.isArray(customLayers) ? customLayers : [];
    const layerIds = [];
    const enabledCompiled = [];

    for (const layer of safeLayers) {
        if (layer && layer.id) {
            layerIds.push(layer.id);
        }
        if (layer && layer.enabled) {
            enabledCompiled.push({
                id: layer.id,
                objectType: normalizeType(layer.objectType),
                predicate: compileLayerPredicate(layer.filters),
            });
        }
    }

    return { enabledCompiled, layerIds };
}

/**
 * Apply pre-compiled layer predicates to a marker set.
 *
 * Same semantics as {@link applyLayerFilters} but accepts pre-compiled
 * predicates from {@link compileLayerPredicates} to avoid redundant
 * compilation on every marker-set change.
 *
 * @param {Array} markers - System-type-filtered markers
 * @param {{ enabledCompiled: Array, layerIds: string[] }} compiled
 * @returns {{ filtered: Array, layerMatchCounts: Object<string, number> }}
 */
export function applyCompiledLayerFilters(markers, compiled) {
    const safeMarkers = Array.isArray(markers) ? markers : [];
    const { enabledCompiled = [], layerIds = [] } = compiled || {};
    const layerMatchCounts = {};

    for (const id of layerIds) {
        layerMatchCounts[id] = 0;
    }

    if (enabledCompiled.length === 0) {
        // No enabled layers → show nothing.  The caller (DiscoverPage) is
        // responsible for passing markers through when layers haven't loaded yet.
        return { filtered: [], layerMatchCounts };
    }

    const matchedIds = new Set();
    const matched = [];

    for (const marker of safeMarkers) {
        const markerType = normalizeType(marker.type);

        for (const layer of enabledCompiled) {
            if (layer.objectType && layer.objectType !== markerType) {
                continue;
            }

            if (layer.predicate(marker)) {
                layerMatchCounts[layer.id] = (layerMatchCounts[layer.id] || 0) + 1;

                if (!matchedIds.has(marker.id)) {
                    matchedIds.add(marker.id);
                    matched.push(marker);
                }
            }
        }
    }

    return { filtered: matched, layerMatchCounts };
}

/**
 * Apply custom layer filters to a set of markers.
 *
 * Follows the visibility pipeline from the plan:
 * 1. Input `markers` should already be filtered by system type toggles.
 * 2. If no custom layers are enabled, return the full input set unchanged.
 * 3. Otherwise, evaluate each enabled custom layer against markers of its type.
 * 4. Return the deduplicated union of all layer matches.
 *
 * A custom layer can never force-show a marker whose system type toggle is
 * disabled — that filtering happens before this function is called.
 *
 * @param {Array} markers - System-type-filtered markers
 * @param {Array} customLayers - Array of { objectType, filters, enabled, ... }
 * @returns {{ filtered: Array, layerMatchCounts: Object<string, number> }}
 */
export function applyLayerFilters(markers, customLayers) {
    const compiled = compileLayerPredicates(customLayers);
    return applyCompiledLayerFilters(markers, compiled);
}
